import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LEAGUES } from '../utils/leagues';
import { validateLeaguePreferenceSelection } from '../utils/leaguePreference';
import './LeaguePreferenceEditor.css';

const leagueByCode = LEAGUES.reduce((acc, league) => {
  acc[league.code] = league;
  return acc;
}, {});

function reorderList(items, fromIndex, toIndex) {
  if (fromIndex === toIndex) return items;
  const next = items.slice();
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

function getDropIndex(clientY, codes, rowRefs) {
  for (let i = 0; i < codes.length; i += 1) {
    const el = rowRefs.current[codes[i]];
    if (!el) continue;
    const { top, height } = el.getBoundingClientRect();
    if (clientY < top + height / 2) return i;
  }
  return Math.max(0, codes.length - 1);
}

function LeagueRowContent({ league, index, selected, isLastSelected, disabled, onToggle, showHandle, handleProps }) {
  return (
    <>
      {showHandle ? (
        <button
          type="button"
          className="league-pref-editor__drag-handle"
          aria-label={`Drag ${league.name} to reorder`}
          {...handleProps}
        >
          ⋮⋮
        </button>
      ) : (
        <span className="league-pref-editor__drag-handle league-pref-editor__drag-handle--disabled" aria-hidden="true" />
      )}
      <label className="league-pref-editor__checkbox-label">
        <input
          type="checkbox"
          className="league-pref-editor__checkbox"
          checked={selected}
          disabled={disabled || isLastSelected}
          onChange={() => onToggle(league.code)}
          aria-label={`${selected ? 'Remove' : 'Add'} ${league.name}`}
        />
      </label>
      {selected ? (
        <span className="league-pref-editor__order-rank">{index + 1}</span>
      ) : (
        <span className="league-pref-editor__order-rank league-pref-editor__order-rank--empty" aria-hidden="true" />
      )}
      <img
        src={`/flags/${league.code.toLowerCase()}.svg`}
        alt=""
        className="league-pref-editor__order-flag"
      />
      <span className="league-pref-editor__order-name">{league.name}</span>
    </>
  );
}

const LeaguePreferenceEditor = ({ value, onChange, disabled = false }) => {
  const selectedCodes = Array.isArray(value) ? value : [];
  const validationError = validateLeaguePreferenceSelection(selectedCodes);
  const rowRefs = useRef({});
  const dragRef = useRef(null);
  const selectedCodesRef = useRef(selectedCodes);
  const onChangeRef = useRef(onChange);
  const [drag, setDrag] = useState(null);
  const canReorder = !disabled && selectedCodes.length > 1;

  selectedCodesRef.current = selectedCodes;
  onChangeRef.current = onChange;

  const unselectedLeagues = useMemo(
    () => LEAGUES.filter((league) => !selectedCodes.includes(league.code)),
    [selectedCodes]
  );

  const displayCodes = useMemo(() => {
    if (!drag) return selectedCodes;
    return reorderList(selectedCodes, drag.fromIndex, drag.overIndex);
  }, [selectedCodes, drag]);

  const toggleLeague = (code) => {
    if (disabled || drag) return;
    const normalized = String(code).toUpperCase();
    if (selectedCodes.includes(normalized)) {
      if (selectedCodes.length <= 1) return;
      onChange(selectedCodes.filter((c) => c !== normalized));
      return;
    }
    onChange([...selectedCodes, normalized]);
  };

  const startDrag = (index, code, event) => {
    if (!canReorder || event.button > 0) return;
    event.preventDefault();
    const rowEl = rowRefs.current[code];
    if (!rowEl) return;
    const rect = rowEl.getBoundingClientRect();
    const nextDrag = {
      code,
      fromIndex: index,
      overIndex: index,
      ghostX: rect.left,
      ghostY: rect.top,
      width: rect.width,
      height: rect.height,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top
    };
    dragRef.current = nextDrag;
    setDrag(nextDrag);
  };

  useEffect(() => {
    if (!drag) return undefined;

    const previousTouchAction = document.body.style.touchAction;
    document.body.style.touchAction = 'none';

    const handlePointerMove = (event) => {
      const current = dragRef.current;
      if (!current) return;
      event.preventDefault();
      const preview = reorderList(selectedCodesRef.current, current.fromIndex, current.overIndex);
      const overIndex = getDropIndex(event.clientY, preview, rowRefs);
      const nextDrag = {
        ...current,
        overIndex,
        ghostX: event.clientX - current.offsetX,
        ghostY: event.clientY - current.offsetY
      };
      dragRef.current = nextDrag;
      setDrag(nextDrag);
    };

    const finishDrag = () => {
      const current = dragRef.current;
      if (!current) return;
      if (current.fromIndex !== current.overIndex) {
        onChangeRef.current(reorderList(selectedCodesRef.current, current.fromIndex, current.overIndex));
      }
      dragRef.current = null;
      setDrag(null);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);

    return () => {
      document.body.style.touchAction = previousTouchAction;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
    };
  }, [Boolean(drag)]);

  const draggingLeague = drag ? leagueByCode[drag.code] : null;

  return (
    <div className={`league-pref-editor ${drag ? 'league-pref-editor--dragging' : ''}`}>
      <p className="league-pref-editor__hint">
        Check the leagues you follow, then drag a row to set their order. At least one league is required.
      </p>

      <ol className="league-pref-editor__order">
        {displayCodes.map((code, index) => {
          const league = leagueByCode[code];
          if (!league) return null;
          const isPlaceholder = drag?.code === code;
          const isLastSelected = selectedCodes.length === 1;

          if (isPlaceholder) {
            return (
              <li
                key={code}
                ref={(el) => {
                  if (el) rowRefs.current[code] = el;
                  else delete rowRefs.current[code];
                }}
                className="league-pref-editor__order-item league-pref-editor__order-item--placeholder"
                style={{ minHeight: drag.height }}
                aria-hidden="true"
              />
            );
          }

          return (
            <li
              key={code}
              ref={(el) => {
                if (el) rowRefs.current[code] = el;
                else delete rowRefs.current[code];
              }}
              className="league-pref-editor__order-item"
            >
              <LeagueRowContent
                league={league}
                index={index}
                selected
                isLastSelected={isLastSelected}
                disabled={disabled}
                onToggle={toggleLeague}
                showHandle={canReorder}
                handleProps={{
                  onPointerDown: (event) => startDrag(selectedCodes.indexOf(code), code, event)
                }}
              />
            </li>
          );
        })}
        {unselectedLeagues.map((league) => (
          <li
            key={league.code}
            className="league-pref-editor__order-item league-pref-editor__order-item--unselected"
          >
            <LeagueRowContent
              league={league}
              index={null}
              selected={false}
              isLastSelected={false}
              disabled={disabled || Boolean(drag)}
              onToggle={toggleLeague}
              showHandle={false}
            />
          </li>
        ))}
      </ol>

      {drag && draggingLeague && createPortal(
        <div
          className="league-pref-editor__ghost"
          style={{
            width: drag.width,
            height: drag.height,
            transform: `translate3d(${drag.ghostX}px, ${drag.ghostY}px, 0)`
          }}
        >
          <LeagueRowContent
            league={draggingLeague}
            index={drag.overIndex + 1}
            selected
            isLastSelected={false}
            disabled
            onToggle={() => {}}
            showHandle
            handleProps={{}}
          />
        </div>,
        document.body
      )}

      {validationError && selectedCodes.length === 0 && (
        <p className="league-pref-editor__error">{validationError}</p>
      )}
    </div>
  );
};

export default LeaguePreferenceEditor;
